import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CameraIcon, CheckCircle2Icon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button, IconButton } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { overlayCardClass, overlayCardSizes, overlayScrimModalClass } from '@/lib/overlayStyles';
import { useSubmitIssueReport } from '@/hooks/useIssueReports';
import { IssueKind, IssueSeverity } from '@/schema/issueReport';
import { captureCanvas, collectDiagnostics } from './collectDiagnostics';
import { useReportProblemStore } from './reportProblemStore';

/**
 * "Report a problem" from anywhere in the app.
 *
 * The value is in what gets attached automatically — route, scan, GPU/renderer, dev log
 * tail, optional canvas capture. A rendering bug that only happens on one driver is
 * otherwise unreproducible from a written description.
 */
export function ReportProblemDialog() {
  const { isOpen, prefill, close } = useReportProblemStore();
  const submit = useSubmitIssueReport();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IssueSeverity>(IssueSeverity.medium);
  const [attachScreenshot, setAttachScreenshot] = useState(true);
  const [diagnostics, setDiagnostics] = useState<ReturnType<typeof collectDiagnostics> | null>(null);

  // Snapshot the environment when the dialog opens, not when it is submitted — by then
  // the user may have navigated somewhere else while writing.
  useEffect(() => {
    if (!isOpen) return;
    setTitle(prefill?.title ?? '');
    setDescription('');
    setSeverity(prefill ? IssueSeverity.high : IssueSeverity.medium);
    setAttachScreenshot(true);
    setDiagnostics(collectDiagnostics());
  }, [isOpen, prefill]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('Give the problem a short title.');
      return;
    }

    const screenshotBase64 = attachScreenshot ? await captureCanvas() : undefined;

    submit.mutate(
      {
        ...(diagnostics ?? {}),
        title: trimmed,
        description: description.trim() || undefined,
        kind: prefill ? IssueKind.autoError : IssueKind.userReport,
        severity,
        errorStack: prefill?.errorStack,
        screenshotBase64,
      },
      {
        onSuccess: () => {
          toast.success('Thanks — the report was sent.', {
            icon: <CheckCircle2Icon className="h-4 w-4" />,
          });
          close();
        },
        onError: () => toast.error('Could not send the report.'),
      },
    );
  };

  return createPortal(
    <div className={cn(overlayScrimModalClass)} onClick={close} role="presentation">
      <div
        className={cn(overlayCardClass, overlayCardSizes.lg)}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-5 pt-5">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Report a problem</p>
          <IconButton onClick={close} variant="ghost-light" size="icon" className="rounded-lg" icon={XIcon} />
        </div>

        <div className="flex flex-col gap-4 px-5 pb-6 pt-3 text-white">
          <div className="flex flex-col gap-2">
            <label className="text-sm text-white/70" htmlFor="report-title">
              What went wrong? *
            </label>
            <Input
              id="report-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Teeth disappear after cutting the gum"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm text-white/70" htmlFor="report-description">
              What were you doing?
            </label>
            <Textarea
              id="report-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Steps you took, what you expected, what happened instead."
              rows={4}
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/70">Severity</span>
              <Select value={severity} onValueChange={(value) => setSeverity(value as IssueSeverity)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {Object.values(IssueSeverity).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
              <Checkbox
                checked={attachScreenshot}
                onCheckedChange={(checked) => setAttachScreenshot(Boolean(checked))}
              />
              <CameraIcon className="h-4 w-4 text-white/40" />
              Attach a screenshot of the 3D view
            </label>
          </div>

          {diagnostics ? (
            <div className="rounded-xl border border-content-highlight/40 bg-content-dark/60 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
                Sent along automatically
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-white/55">
                <li>Page: {diagnostics.route}</li>
                {diagnostics.scanId ? <li>Scan: {diagnostics.scanId}</li> : null}
                <li>Graphics: {diagnostics.gpuRenderer || 'unavailable'}</li>
                <li>Screen: {diagnostics.screenInfo}</li>
              </ul>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost-light" onClick={close}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={submit.isPending}>
              {submit.isPending ? 'Sending…' : 'Send report'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
